import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import api, { formatApiErrorDetail } from "../lib/api";
import { Seo } from "../components/Layout";

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", published: true };

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === null) nav("/login");
  }, [user, nav]);

  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user]);

  async function refresh() {
    try {
      const [p, c] = await Promise.all([
        api.get("/admin/blog"),
        api.get("/admin/contacts"),
      ]);
      setPosts(p.data);
      setContacts(c.data);
    } catch (e) {
      toast.error("Impossible de charger les données.");
    }
  }

  function startNew() {
    setEditing("new");
    setForm(EMPTY);
  }
  function startEdit(p) {
    setEditing(p.id);
    setForm({
      title: p.title,
      excerpt: p.excerpt,
      content: p.content,
      cover_image: p.cover_image || "",
      published: p.published,
    });
  }
  function cancel() {
    setEditing(null);
    setForm(EMPTY);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, cover_image: form.cover_image || null };
      if (editing === "new") {
        await api.post("/admin/blog", payload);
        toast.success("Article créé.");
      } else {
        await api.put(`/admin/blog/${editing}`, payload);
        toast.success("Article modifié.");
      }
      cancel();
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(p) {
    if (!window.confirm(`Supprimer "${p.title}" ?`)) return;
    try {
      await api.delete(`/admin/blog/${p.id}`);
      toast.success("Article supprimé.");
      refresh();
    } catch {
      toast.error("Suppression impossible.");
    }
  }

  if (user === undefined) return <div className="pt-40 text-center text-sand-600">Chargement…</div>;
  if (!user) return null;

  return (
    <>
      <Seo title="Admin" />
      <section className="pt-32 pb-32" data-testid="admin-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <div>
              <p className="eyebrow mb-2">Administration</p>
              <h1 className="text-4xl font-serif text-sand-900">Bienvenue, {user.name || "Valentine"}</h1>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="btn-outline">
              <LogOut size={16} /> Se déconnecter
            </button>
          </div>

          <div className="flex gap-3 mb-8" data-testid="admin-tabs">
            <Tab active={tab === "posts"} onClick={() => setTab("posts")} testid="tab-posts">Articles ({posts.length})</Tab>
            <Tab active={tab === "contacts"} onClick={() => setTab("contacts")} testid="tab-contacts">Messages ({contacts.length})</Tab>
            <Tab active={tab === "leads"} onClick={() => setTab("leads")} testid="tab-leads">Leads ({leads.length})</Tab>
          </div>

          {tab === "posts" && (
            <div>
              {!editing ? (
                <>
                  <button onClick={startNew} data-testid="new-post-btn" className="btn-primary mb-6">
                    <Plus size={16} /> Nouvel article
                  </button>
                  <div className="space-y-4">
                    {posts.length === 0 && <p className="text-sand-600">Aucun article.</p>}
                    {posts.map((p) => (
                      <article key={p.id} className="flex flex-wrap items-center gap-4 p-5 rounded-2xl border border-sand-300/60 bg-white/60" data-testid={`post-row-${p.slug}`}>
                        {p.cover_image && <img src={p.cover_image} alt="" loading="lazy" className="w-20 h-20 rounded-lg object-cover" />}
                        <div className="flex-1 min-w-[200px]">
                          <h3 className="font-serif text-lg text-sand-900">{p.title}</h3>
                          <p className="text-sm text-sand-600 line-clamp-1">{p.excerpt}</p>
                          <p className="text-xs text-sand-500 mt-1">{p.published ? "Publié" : "Brouillon"} · /{p.slug}</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => startEdit(p)} data-testid={`edit-${p.slug}`} className="p-2 rounded-lg hover:bg-sand-200">
                            <Pencil size={16} />
                          </button>
                          <button onClick={() => remove(p)} data-testid={`delete-${p.slug}`} className="p-2 rounded-lg hover:bg-red-100 text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                </>
              ) : (
                <form onSubmit={save} className="rounded-2xl border border-sand-300/60 bg-white/70 p-8 space-y-5" data-testid="post-form">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-serif text-sand-900">{editing === "new" ? "Nouvel article" : "Modifier l'article"}</h2>
                    <button type="button" onClick={cancel} className="p-2 rounded-lg hover:bg-sand-200">
                      <X size={18} />
                    </button>
                  </div>
                  <Input label="Titre" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="form-title" />
                  <Input label="Extrait (résumé)" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} required testid="form-excerpt" />
                  <Input label="Image de couverture (URL)" value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} testid="form-cover" />
                  <div>
                    <label className="block text-sm text-sand-700 mb-2">Contenu (laisser une ligne vide entre les paragraphes)</label>
                    <textarea
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      rows={14}
                      required
                      data-testid="form-content"
                      className="w-full rounded-xl border border-sand-400 bg-white px-5 py-4 focus:outline-none focus:border-sand-500 font-sans"
                    />
                  </div>
                  <label className="inline-flex items-center gap-3 text-sand-700">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="form-published" />
                    Publié (visible publiquement)
                  </label>
                  <div className="flex gap-3">
                    <button type="submit" disabled={saving} data-testid="form-save" className="btn-primary disabled:opacity-60">
                      {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                    <button type="button" onClick={cancel} className="btn-outline">Annuler</button>
                  </div>
                </form>
              )}
            </div>
          )}

          {tab === "contacts" && (
            <div className="space-y-4" data-testid="admin-contacts-list">
              {contacts.length === 0 && <p className="text-sand-600">Aucun message reçu.</p>}
              {contacts.map((c) => (
                <article key={c.id} className="p-5 rounded-2xl border border-sand-300/60 bg-white/60" data-testid={`contact-row-${c.id}`}>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="font-medium text-sand-900">{c.name}</h3>
                    <a href={`mailto:${c.email}`} className="text-sand-600 text-sm">{c.email}</a>
                    {c.phone && <span className="text-sand-600 text-sm">· {c.phone}</span>}
                    <span className="text-sand-500 text-xs ml-auto">{new Date(c.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sand-700">{c.message}</p>
                </article>
              ))}
            </div>
          )}

          {tab === "leads" && (
            <div data-testid="admin-leads-list">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <p className="text-sand-700 text-sm">
                  {leads.length} contact{leads.length > 1 ? "s" : ""} collecté{leads.length > 1 ? "s" : ""} via les guides gratuits.
                </p>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads.csv`}
                  download
                  data-testid="export-leads-csv"
                  className="btn-outline text-sm"
                >
                  Exporter en CSV
                </a>
              </div>
              {leads.length === 0 && <p className="text-sand-600">Aucun lead pour le moment.</p>}
              <div className="space-y-3">
                {leads.map((l) => (
                  <article key={l.id} className="p-4 rounded-2xl border border-sand-300/60 bg-white/60" data-testid={`lead-row-${l.id}`}>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h3 className="font-medium text-sand-900">{l.first_name} {l.last_name}</h3>
                      <a href={`mailto:${l.email}`} className="text-sand-600 text-sm">{l.email}</a>
                      <span className="text-sand-500 text-xs ml-auto">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
                    </div>
                    <p className="mt-1 text-xs text-sand-500 italic">{l.magnet_title}</p>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function Tab({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-5 py-2 rounded-full text-sm transition-all ${active ? "bg-sand-900 text-sand-50" : "bg-sand-200/60 text-sand-700 hover:bg-sand-300"}`}
    >
      {children}
    </button>
  );
}

function Input({ label, value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-sm text-sand-700 mb-2">{label}{required && " *"}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-sand-400 bg-white px-5 py-3 focus:outline-none focus:border-sand-500"
      />
    </div>
  );
}
